import { useState } from "react";
import { useNavigate } from "react-router";
import { Lock } from "lucide-react";
import { formStateFor } from "@/modules/identity/commands/authCopy";
import { formatLebanesePhone } from "@/modules/identity/surfaces";
import { Banner, Field } from "@/design/ui";
import { AuthShell } from "~/components/AuthShell";
import { FootLink, SubmitButton } from "~/components/forms";
import { useMe } from "~/auth/MeProvider";
import { adoptSession } from "~/auth/supabase";
import { post } from "~/lib/api";
import { useForm } from "~/lib/useForm";

/**
 * StaffLogInScreen (docs/ux/screens/StaffLogInScreen.md), H47.
 *
 * **One identifier for the whole business side, the number.** It is the same
 * field the consumer log-in uses, deliberately: since H47 there is one way to
 * be identified in this app and one way to prove it, and a member who was added
 * by a Manager (H44) types exactly what an owner who registered their own
 * clinic types.
 *
 * **The session travels with all three outcomes, including the two gates**, and
 * it has to: they are reached *after* Supabase signed the person in, so a
 * client that discarded the tokens on a gate would lock them out of the very
 * screen the gate sends them to. Verification unmasks that session (H47), and
 * the password change re-authenticates with it (H44).
 */
type StaffNext = "verify" | "password_change" | "business";

interface StaffLogInResult {
  next: StaffNext;
  phone?: string;
  session: { accessToken: string; refreshToken: string };
}

export function StaffLogInScreen() {
  const navigate = useNavigate();
  const { reload } = useMe();
  const { state, pending, submit } = useForm();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = await submit<StaffLogInResult>(
      () => post("/auth/staff/login", { phone, password }),
      formStateFor,
      { phone },
    );
    if (!result) return;

    await adoptSession(result.session);
    await reload();

    if (result.next === "verify") {
      const query = new URLSearchParams({
        phone: result.phone ?? phone,
        role: "member",
        unverified: "1",
      });
      void navigate(`/verify?${query.toString()}`);
      return;
    }
    void navigate(result.next === "password_change" ? "/staff/password" : "/business", {
      replace: true,
    });
  }

  return (
    <AuthShell>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-ink">Welcome back</h1>
        <p className="mt-1 text-sm text-muted">
          Log in to run your clinic on Bookr
        </p>
      </div>

      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          <Field
            label="Phone number"
            name="phone"
            type="tel"
            inputMode="tel"
            placeholder="71 234 456"
            prefix="+961"
            value={phone}
            onChange={(v) => setPhone(formatLebanesePhone(v))}
            error={state.fieldErrors?.phone}
            autoComplete="tel"
          />
          <Field
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={setPassword}
            error={state.fieldErrors?.password}
            autoComplete="current-password"
            icon={Lock}
          />
          <SubmitButton pending={pending} pendingLabel="Logging in…">
            Log in
          </SubmitButton>
        </fieldset>
      </form>

      <FootLink lead="Own a clinic or practice?" to="/join" className="mt-6">
        Register your business
      </FootLink>
      <FootLink lead="Looking to book?" to="/login">
        Customer log in
      </FootLink>
    </AuthShell>
  );
}
